import { useEffect, useMemo, useRef, useState } from 'react';

import { TOOL_IDS, useCurrentProject } from '@adsk/alloy-provider';

import { isPluginProductOrToolNotAvailableInEntitlements } from '../helpers/isPluginProductOrToolNotAvailableInEntitlements';

export const usePluginResolver = (unresolvedPlugins, onPluginsLoadError) => {
  const currentProject = useCurrentProject();

  const hasPluginPromises = useMemo(
    () => unresolvedPlugins.some((plugin) => plugin instanceof Promise),
    [unresolvedPlugins],
  );

  const [plugins, setPlugins] = useState(
    hasPluginPromises ? [] : unresolvedPlugins,
  );

  const [loadingPlugins, setLoadingPlugins] = useState(
    hasPluginPromises || !currentProject,
  );

  const resolvingPlugins = useRef(false);

  useEffect(() => {
    const resolvePlugins = async () => {
      resolvingPlugins.current = true;

      const resolvedPluginResults = await Promise.allSettled(unresolvedPlugins);

      const plugins = resolvedPluginResults
        .filter(({ status }) => status === 'fulfilled')
        .map(({ value }) => value)
        .flat(2);

      const pluginLoadErrors = resolvedPluginResults
        .filter(({ status }) => status === 'rejected')
        .map(({ reason }) => reason);

      if (pluginLoadErrors.length > 0) {
        onPluginsLoadError?.(pluginLoadErrors);
      }

      setPlugins(plugins);
      setLoadingPlugins(false);

      resolvingPlugins.current = false;
    };

    const shouldResolvePlugins =
      currentProject && hasPluginPromises && !resolvingPlugins.current;

    if (shouldResolvePlugins) {
      resolvePlugins();
    } else if (currentProject && !hasPluginPromises) {
      setLoadingPlugins(false);
    }
  }, [
    currentProject,
    hasPluginPromises,
    setLoadingPlugins,
    setPlugins,
    onPluginsLoadError,
    resolvingPlugins,
    unresolvedPlugins,
  ]);

  const allowedPlugins = useMemo(() => {
    if (!currentProject || loadingPlugins) {
      return [];
    }

    return plugins.filter(
      (plugin) =>
        currentProject &&
        !isPluginProductOrToolNotAvailableInEntitlements({
          plugin,
          currentProject,
          bypassToolIds: [TOOL_IDS.PHOTOS],
        }),
    );
  }, [currentProject, plugins, loadingPlugins]);

  return {
    plugins,
    allowedPlugins,
    loadingPlugins,
  };
};
